/**
 * ConcentricLayout - React SVG rendering with ring math positioning
 *
 * Renders the ego network in concentric rings (Inner/Community/Network).
 * Uses pure React SVG elements (no D3 DOM manipulation).
 */

import React, { useMemo, useRef, useCallback } from 'react';
import { Box, useTheme } from '@mui/material';
import PersonNode from './PersonNode';
import ConnectionEdge from './ConnectionEdge';
import { ZONE_CONFIG, CONCENTRIC_LAYOUT, ZONES } from '../../constants/dashboardConfig';
import { calculateConcentricPositions } from '../../utils/networkUtils';

// eslint-disable-next-line max-lines-per-function
function ConcentricLayout({
  nodes,
  edges,
  egoId,
  selectedNode,
  selectedEdge,
  onNodeSelect,
  onEdgeSelect,
  onDeselect,
}) {
  const containerRef = useRef(null);
  const theme = useTheme();

  // Calculate SVG viewport dimensions
  const width = 900;
  const height = 700;
  const centerX = width * 0.48;
  const centerY = height * 0.48;

  // Position nodes on concentric rings
  const positionedNodes = useMemo(
    () => calculateConcentricPositions(nodes, egoId, centerX, centerY),
    [nodes, egoId, centerX, centerY]
  );

  // Build node lookup for edge rendering
  const nodeMap = useMemo(() => {
    const map = {};
    positionedNodes.forEach((n) => {
      map[n.id] = n;
    });
    return map;
  }, [positionedNodes]);

  // Determine which nodes/edges are highlighted (connected to selection)
  const { highlightedNodeIds, highlightedEdges } = useMemo(() => {
    const nodeIds = new Set();
    const edgeSet = new Set();

    if (selectedNode) {
      edges.forEach((e) => {
        if (e.source === selectedNode.id || e.target === selectedNode.id) {
          nodeIds.add(e.source);
          nodeIds.add(e.target);
          edgeSet.add(`${e.source}-${e.target}`);
        }
      });
    }
    if (selectedEdge) {
      nodeIds.add(selectedEdge.source);
      nodeIds.add(selectedEdge.target);
      edgeSet.add(`${selectedEdge.source}-${selectedEdge.target}`);
    }

    return { highlightedNodeIds: nodeIds, highlightedEdges: edgeSet };
  }, [selectedNode, selectedEdge, edges]);

  const hasSelection = selectedNode || selectedEdge;

  // Click on empty space to deselect
  const handleBackgroundClick = useCallback(() => {
    onDeselect();
  }, [onDeselect]);

  // Ring radii for background circles
  const rings = [
    { zone: ZONES.INNER, radius: CONCENTRIC_LAYOUT.ringRadii[ZONES.INNER] },
    { zone: ZONES.COMMUNITY, radius: CONCENTRIC_LAYOUT.ringRadii[ZONES.COMMUNITY] },
    { zone: ZONES.NETWORK, radius: CONCENTRIC_LAYOUT.ringRadii[ZONES.NETWORK] },
  ];

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pb: 7, // space for timeline bar
      }}
    >
      <svg
        role="img"
        aria-label="Ego network visualization showing relationships between people"
        viewBox={`0 0 ${width} ${height}`}
        style={{
          width: '100%',
          height: '100%',
          maxWidth: width,
          maxHeight: height,
        }}
        onClick={handleBackgroundClick}
      >
        {/* Live region for screen reader announcements */}
        <title>Ego Network Visualization</title>
        <desc>
          Interactive network showing {nodes.length} people arranged in concentric zones
          by relationship proximity.
        </desc>

        {/* Concentric ring guides */}
        {rings.map(({ zone, radius }) => (
          <g key={zone}>
            <circle
              cx={centerX}
              cy={centerY}
              r={radius}
              fill="none"
              stroke={ZONE_CONFIG[zone].ringBorderColor}
              strokeWidth={1.5}
              strokeDasharray="8 4"
            />
            {/* Zone label */}
            <text
              x={centerX + radius + 8}
              y={centerY - 4}
              fill={theme.palette.text.disabled}
              fontSize={10}
              fontWeight={600}
              fontFamily="DM Sans, sans-serif"
              textTransform="uppercase"
              letterSpacing={1.5}
              style={{ textTransform: 'uppercase' }}
            >
              {ZONE_CONFIG[zone].label}
            </text>
          </g>
        ))}

        {/* Edges (rendered below nodes) */}
        <g>
          {edges.map((edge) => {
            const sourceNode = nodeMap[edge.source];
            const targetNode = nodeMap[edge.target];
            const edgeKey = `${edge.source}-${edge.target}`;
            const isEdgeSelected =
              selectedEdge &&
              selectedEdge.source === edge.source &&
              selectedEdge.target === edge.target;
            const isEdgeHighlighted = highlightedEdges.has(edgeKey);

            return (
              <ConnectionEdge
                key={edgeKey}
                edge={edge}
                sourceNode={sourceNode}
                targetNode={targetNode}
                isSelected={isEdgeSelected}
                isHighlighted={isEdgeHighlighted && !isEdgeSelected}
                onSelect={onEdgeSelect}
              />
            );
          })}
        </g>

        {/* Nodes */}
        <g>
          {positionedNodes.map((node) => {
            const isNodeSelected = selectedNode?.id === node.id;
            const isNodeHighlighted = hasSelection && highlightedNodeIds.has(node.id);

            return (
              <PersonNode
                key={node.id}
                node={node}
                isSelected={isNodeSelected}
                isHighlighted={!hasSelection || isNodeHighlighted || isNodeSelected}
                onSelect={onNodeSelect}
              />
            );
          })}
        </g>
      </svg>
    </Box>
  );
}

export default ConcentricLayout;
